import { useState } from "react";
import { useAdminApp } from "@/hooks/useAdminApp";
import { useStateMachineValue } from "@/core/arch/state_machine";
import { Button } from "@/components/ui/base/button";
import { Input } from "@/components/ui/base/input";
import { Label } from "@/components/ui/base/label";
import { AddressAutocomplete } from "@/components/ui/AddressAutocomplete";
import { selectClass } from "@/components/ui/styles";
import {
  PROVINCES,
  PROVINCE_LABELS,
  US_STATES,
  US_STATE_LABELS,
  type GooglePlacesAddressParsed,
} from "@cpos/shared";
import type { AddressData } from "@/application/provisioning/types";

function AddressForm({
  label,
  prefix,
  value,
  onChange,
  errors,
  country,
}: {
  label: string;
  prefix: string;
  value: Partial<AddressData>;
  onChange: (addr: AddressData) => void;
  errors: Record<string, string>;
  country: "CA" | "US";
}) {
  const update = (field: keyof AddressData, val: string) => {
    onChange({
      line1: value.line1 ?? "",
      city: value.city ?? "",
      state: value.state ?? "",
      postalCode: value.postalCode ?? "",
      country: value.country ?? country,
      ...value,
      [field]: val,
    });
  };

  const handleAddressSelected = (parsed: GooglePlacesAddressParsed) => {
    onChange({
      line1: parsed.line1 ?? value.line1 ?? "",
      line2: value.line2,
      city: parsed.city ?? value.city ?? "",
      state: parsed.state ?? value.state ?? "",
      postalCode: parsed.postalCode ?? value.postalCode ?? "",
      country: parsed.country ?? country,
    });
  };

  const regions = country === "CA" ? ["ca"] : ["us"];
  const stateOptions = country === "CA" ? PROVINCES : US_STATES;
  const stateLabels = country === "CA" ? PROVINCE_LABELS : US_STATE_LABELS;
  const stateError = errors[`${prefix}.state`];
  const line1Error = errors[`${prefix}.line1`];
  const cityError = errors[`${prefix}.city`];
  const postalError = errors[`${prefix}.postalCode`];

  return (
    <div>
      <h3 className="text-foreground mb-3 text-sm font-semibold">{label}</h3>
      <div className="grid grid-cols-2 gap-5">
        <div className="col-span-2">
          <Label>Address Line 1</Label>
          <AddressAutocomplete
            value={value.line1 ?? ""}
            onChange={(val) => update("line1", val)}
            onAddressSelected={handleAddressSelected}
            regions={regions}
            error={line1Error}
          />
          {line1Error && (
            <p className="mt-1 text-xs text-red-600">{line1Error}</p>
          )}
        </div>
        <div className="col-span-2">
          <Label>Address Line 2 (optional)</Label>
          <Input
            value={value.line2 ?? ""}
            onChange={(e) => update("line2", e.target.value)}
          />
        </div>
        <div>
          <Label>City</Label>
          <Input
            value={value.city ?? ""}
            onChange={(e) => update("city", e.target.value)}
          />
          {cityError && (
            <p className="mt-1 text-xs text-red-600">{cityError}</p>
          )}
        </div>
        <div>
          <Label>{country === "CA" ? "Province" : "State"}</Label>
          <select
            value={value.state ?? ""}
            onChange={(e) => update("state", e.target.value)}
            className={selectClass(stateError)}
          >
            <option value="">
              Select {country === "CA" ? "province" : "state"}...
            </option>
            {stateOptions.map((code) => (
              <option key={code} value={code}>
                {stateLabels[code as keyof typeof stateLabels]} ({code})
              </option>
            ))}
          </select>
          {stateError && (
            <p className="mt-1 text-xs text-red-600">{stateError}</p>
          )}
        </div>
        <div>
          <Label>{country === "CA" ? "Postal Code" : "ZIP Code"}</Label>
          <Input
            value={value.postalCode ?? ""}
            onChange={(e) => update("postalCode", e.target.value)}
          />
          {postalError && (
            <p className="mt-1 text-xs text-red-600">{postalError}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export function AddressStep() {
  const { provisioningBloc } = useAdminApp();
  const [state] = useStateMachineValue(provisioningBloc);
  const { formData, fieldErrors } = state;
  const country = formData.businessCountry ?? "CA";
  const [sameAsBusiness, setSameAsBusiness] = useState(() => {
    const biz = formData.businessAddress;
    const ind = formData.individualAddress;
    if (!biz || !ind) return false;
    return (
      biz.line1 === ind.line1 &&
      (biz.line2 ?? "") === (ind.line2 ?? "") &&
      biz.city === ind.city &&
      biz.state === ind.state &&
      biz.postalCode === ind.postalCode &&
      biz.country === ind.country
    );
  });

  const handleSameToggle = (checked: boolean) => {
    setSameAsBusiness(checked);
    if (checked && formData.businessAddress) {
      provisioningBloc.updateField("individualAddress", {
        ...formData.businessAddress,
      } as AddressData);
    }
  };

  return (
    <div>
      <h2 className="border-border text-foreground mb-6 border-b pb-4 text-lg font-semibold">
        Addresses
      </h2>

      <AddressForm
        label="Business Address"
        prefix="businessAddress"
        value={formData.businessAddress ?? {}}
        onChange={(addr) => {
          provisioningBloc.updateField("businessAddress", addr);
          if (sameAsBusiness) {
            provisioningBloc.updateField("individualAddress", { ...addr });
          }
        }}
        errors={fieldErrors}
        country={country}
      />

      <div className="my-6 border-t" />

      <div className="mb-4 flex items-center gap-2">
        <input
          type="checkbox"
          id="sameAsBusiness"
          checked={sameAsBusiness}
          onChange={(e) => handleSameToggle(e.target.checked)}
          className="border-border h-4 w-4 rounded text-blue-600 focus:ring-blue-500/20"
        />
        <Label htmlFor="sameAsBusiness" className="mb-0 cursor-pointer text-sm">
          Individual address same as business
        </Label>
      </div>

      {!sameAsBusiness && (
        <AddressForm
          label="Individual / Owner Address"
          prefix="individualAddress"
          value={formData.individualAddress ?? {}}
          onChange={(addr) =>
            provisioningBloc.updateField("individualAddress", addr)
          }
          errors={fieldErrors}
          country={country}
        />
      )}

      <div className="border-border mt-8 flex justify-between border-t pt-6">
        <Button variant="outline" onClick={() => provisioningBloc.prevStep()}>
          ← Back
        </Button>
        <Button onClick={() => provisioningBloc.nextStep()}>
          Next → Review
        </Button>
      </div>
    </div>
  );
}
